import { type ChangeEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { RecordUpdateLifecycle } from "~/common/types/graphql";
import { Badge, Button, Checkbox, Label, Textarea } from "~/components/ui";
import { useProceedRecordUpdate } from "~/features/dataImportProjects/hooks/useProceedRecordUpdate";
import { useRecordUpdatePreview } from "~/features/dataImportProjects/hooks/useRecordUpdatePreview";
import { RecordUpdatePreview } from "./RecordUpdatePreview";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Split the free-text input on any whitespace/comma and keep valid UUIDs. */
function parseRecordIds(text: string): string[] {
  const ids = text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => UUID_RE.test(s));
  return [...new Set(ids)];
}

interface RecordUpdateForEditProps {
  /**
   * Called after a record-update DIP is created. Receives the new DIP id (null
   * if the backend created no DIP, e.g. every record was unresolvable). The
   * container closes the dialog and navigates to the DIP.
   */
  onCreated: (dataImportProjectId: string | null) => void;
  /**
   * The record-update lifecycle the DIP runs (mirrors the backend `RecordUpdateLifecycle`):
   *   - "edit" (default): recompose the pasted records and their lineage siblings from evidence.
   *   - "deactivate": deactivate only the pasted records (set deactivatedAt); the backend scopes the
   *     preview to the selected records, so siblings never appear and the sibling gate does not apply.
   */
  lifecycle?: RecordUpdateLifecycle;
}

/**
 * "For edit" / "bulk deactivate" branch of the create dialog: the operator
 * pastes RecordIDs, runs the read-only blast-radius preview, then proceeds to
 * create the record-update DIP. In edit mode a sibling acknowledgement is
 * required (editing recomposes same-lineage siblings too); in deactivate mode
 * only the pasted records are deactivated, so there is no sibling gate.
 * Proceed is disabled while the preview reports conflicting or missing
 * evidence-lineage identities — the backend refuses those outright (#7203),
 * so we do not let the operator hit the refusal blind.
 */
export function RecordUpdateForEdit({ onCreated, lifecycle = "edit" }: RecordUpdateForEditProps) {
  const { t } = useTranslation();
  const { preview, loading, run, reset } = useRecordUpdatePreview();
  const { proceed, proceeding } = useProceedRecordUpdate();

  const [text, setText] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const recordIds = useMemo(() => parseRecordIds(text), [text]);
  const isDeactivate = lifecycle === "deactivate";
  const siblingCount = preview ? preview.records.filter((r) => !r.selected).length : 0;
  const relationSiblingCount = preview ? preview.records.filter((r) => !r.selected && r.kind === "relation").length : 0;
  // Deactivate targets only the pasted records — siblings are never deactivated — so the sibling
  // acknowledgement gate applies to edit mode only.
  const gateSatisfied = isDeactivate || siblingCount === 0 || confirmed;
  // The backend refuses proceed outright on either condition (#7203), so block it here with an explanation.
  const blockedReasonKey =
    preview && preview.counts.toIdentify > 0
      ? "feature.recordUpdate.proceed.blockedIdentityMissing"
      : preview && preview.counts.conflict > 0
        ? "feature.recordUpdate.proceed.blockedConflict"
        : null;
  const canProceed =
    Boolean(preview) && (preview?.records.length ?? 0) > 0 && gateSatisfied && !blockedReasonKey && !proceeding;

  const onTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    // Editing the IDs invalidates the previewed blast radius and its
    // confirmationToken; drop the stale preview so proceed cannot submit the
    // new IDs under the old token, and force a fresh preview before it re-enables.
    if (preview) {
      setConfirmed(false);
      reset();
    }
  };

  const onPreview = async () => {
    setConfirmed(false);
    await run(recordIds, lifecycle);
  };

  const onProceed = async () => {
    if (!preview) {
      return;
    }
    const res = await proceed(recordIds, preview.confirmationToken, lifecycle);
    if (res) {
      // Literal keys only — i18next-cli prunes keys it cannot see statically.
      toast.success(
        isDeactivate ? t("feature.recordDeactivate.proceed.success") : t("feature.recordUpdate.proceed.success"),
      );
      onCreated(res.dataImportProjectId ?? null);
    }
  };

  const onClear = () => {
    setText("");
    setConfirmed(false);
    reset();
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-14-regular text-fg-2">
        {isDeactivate ? t("feature.recordDeactivate.description") : t("feature.recordUpdate.description")}
      </p>

      <div className="flex flex-col gap-2">
        <Label htmlFor="record-update-ids">{t("feature.recordUpdate.input.label")}</Label>
        <Textarea
          id="record-update-ids"
          rows={4}
          placeholder={t("feature.recordUpdate.input.placeholder")}
          value={text}
          onChange={onTextChange}
        />
        <div className="flex items-center gap-3">
          <Button onClick={onPreview} disabled={recordIds.length === 0 || loading}>
            {loading ? t("feature.recordUpdate.preview.running") : t("feature.recordUpdate.preview.run")}
          </Button>
          <Button variant="ghost" onClick={onClear}>
            {t("ui.reset")}
          </Button>
          <span className="text-12-regular text-fg-3">
            {t("feature.recordUpdate.input.parsedCount", { count: recordIds.length })}
          </span>
        </div>
      </div>

      {preview ? (
        <>
          <RecordUpdatePreview preview={preview} />

          {blockedReasonKey ? (
            <div className="rounded-[var(--radius-md)] border border-border-default bg-score-c-bg p-3 text-14-regular text-score-c">
              {t(blockedReasonKey)}
            </div>
          ) : null}

          {!isDeactivate && siblingCount > 0 ? (
            <div className="flex items-start gap-3 rounded-[var(--radius-md)] border border-border-default p-3">
              <Checkbox
                id="record-update-confirm"
                checked={confirmed}
                onCheckedChange={(v) => setConfirmed(v === true)}
              />
              <Label htmlFor="record-update-confirm" className="text-fg-2">
                {relationSiblingCount > 0
                  ? t("feature.recordUpdate.proceed.siblingAckWithRelations", {
                      count: siblingCount,
                      relationCount: relationSiblingCount,
                    })
                  : t("feature.recordUpdate.proceed.siblingAck", { count: siblingCount })}
              </Label>
            </div>
          ) : null}

          <div className="flex items-center gap-3">
            <Button variant="success" onClick={onProceed} disabled={!canProceed}>
              {isDeactivate
                ? proceeding
                  ? t("feature.recordDeactivate.proceed.running")
                  : t("feature.recordDeactivate.proceed.run")
                : proceeding
                  ? t("feature.recordUpdate.proceed.running")
                  : t("feature.recordUpdate.proceed.run")}
            </Button>
            {!isDeactivate && siblingCount > 0 ? (
              <Badge tone="status-review">
                {t("feature.recordUpdate.proceed.siblingBadge", { count: siblingCount })}
              </Badge>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
